import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactListAPI, ContactList } from '../services/api';
import Icon from '../components/Icon';
import Empty from '../components/ui/Empty';
import { fmt } from '../utils/format';
import { useT } from '../i18n/LanguageContext';

const ContactLists: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const [lists, setLists] = useState<ContactList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create list form state
  const [newList, setNewList] = useState({ name: '', description: '' });

  useEffect(() => {
    loadLists();
  }, [page, search]);

  const loadLists = async () => {
    try {
      setLoading(true);
      const response = await contactListAPI.getAll({
        page,
        limit: 20,
        search,
      });

      if (response.data.success) {
        setLists(response.data.data.lists);
        setTotalPages(response.data.data.pages);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (!newList.name) {
      setError(t('Ime liste je obavezno', 'List name is required'));
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await contactListAPI.create(newList);

      if (response.data.success) {
        setSuccess(t('Lista uspešno kreirana!', 'List created successfully!'));
        setShowCreateModal(false);
        setNewList({ name: '', description: '' });
        loadLists();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDeleteList = async (id: number) => {
    if (!window.confirm(t('Da li ste sigurni da želite da obrišete ovu listu?', 'Are you sure you want to delete this list?'))) {
      return;
    }

    try {
      await contactListAPI.delete(id);
      setSuccess(t('Lista uspešno obrisana!', 'List deleted successfully!'));
      loadLists();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadLists();
  };

  if (loading && lists.length === 0) {
    return (
      <div className="page">
        <div className="card card-pad muted">{t('Učitavanje lista…', 'Loading lists…')}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-title">{t('Liste Kontakata', 'Contact Lists')}</div>
          <div className="page-sub">{t('Organizuj kontakte u liste i šalji ciljane kampanje.', 'Organize contacts into lists and send targeted campaigns.')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Icon name="plus" size={16} /> {t('Nova lista', 'New list')}
        </button>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--ok)', color: 'var(--ok)', marginBottom: 16 }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      <form onSubmit={handleSearch} className="flex items-center justify-between wrap gap-16" style={{ marginBottom: 18 }}>
        <div className="search">
          <Icon name="search" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Pretraži liste', 'Search lists')}
          />
        </div>
      </form>

      {lists.length === 0 ? (
        <div className="card">
          <Empty
            icon="lists"
            title={t('Nema lista', 'No lists')}
            sub={t('Kreirajte prvu listu kontakata da biste počeli.', 'Create your first contact list to get started.')}
            action={
              <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                <Icon name="plus" size={16} /> {t('Nova lista', 'New list')}
              </button>
            }
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)' }}>
          {lists.map((list) => (
            <div
              key={list.id}
              className="card card-pad card-hover"
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
              onClick={() => navigate(`/contact-lists/${list.id}`)}
            >
              <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 13,
                    background: 'var(--brand-50)',
                    color: 'var(--brand-600)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon name="lists" size={20} />
                </span>
                <span className="chip">
                  <b className="tnum">{fmt(list.contactCount || 0)}</b> {t('kontakata', 'contacts')}
                </span>
              </div>

              <div className="card-title">{list.name}</div>
              <p className="muted" style={{ fontSize: 13, marginTop: 4, minHeight: 38 }}>
                {list.description || t('Bez opisa', 'No description')}
              </p>

              <div
                className="flex items-center gap-8"
                style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn btn-secondary btn-sm grow"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/contact-lists/${list.id}`);
                  }}
                >
                  <Icon name="eye" size={14} /> {t('Pregled', 'View')}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--danger)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteList(list.id);
                  }}
                >
                  <Icon name="trash" size={14} /> {t('Obriši', 'Delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-16 mt-16">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            <Icon name="chevronLeft" size={16} /> {t('Prethodna', 'Previous')}
          </button>
          <span className="muted tnum" style={{ fontSize: 13 }}>
            {t('Strana', 'Page')} {page} {t('od', 'of')} {totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            {t('Sledeća', 'Next')} <Icon name="arrowRight" size={16} />
          </button>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="card-title">{t('Nova lista', 'New list')}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateModal(false)}>
                <Icon name="x" size={16} /> {t('Zatvori', 'Close')}
              </button>
            </div>

            <div className="modal-body">
              <div className="field">
                <label className="label">{t('Naziv liste *', 'List name *')}</label>
                <input
                  className="input"
                  type="text"
                  value={newList.name}
                  onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                  placeholder={t('npr. Newsletter pretplatnici', 'e.g. Newsletter subscribers')}
                  autoFocus
                  required
                />
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">{t('Opis', 'Description')}</label>
                <textarea
                  className="textarea"
                  value={newList.description}
                  onChange={(e) => setNewList({ ...newList, description: e.target.value })}
                  placeholder={t('Kratko objašnjenje ko je u listi (opciono)', 'A short explanation of who is on the list (optional)')}
                  style={{ minHeight: 100 }}
                />
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>
                {t('Otkaži', 'Cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleCreateList}>
                <Icon name="checkCircle" size={16} /> {t('Kreiraj listu', 'Create list')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactLists;
